import type { DocSection } from '../types'

export const supervisionSection: DocSection = {
  id: 'supervision',
  title: 'Supervision & statistiques',
  icon: 'eye',
  accent: '#0891B2',
  summary: "Piloter en temps réel : écouter/souffler/intervenir, suivre le temps des agents, analyser les résultats.",
  minRole: 'team_admin',
  articles: [
    {
      id: 'live',
      title: 'Supervision en temps réel',
      summary: 'Voir les agents, les appels en cours, les alertes.',
      icon: 'eye',
      blocks: [
        { type: 'p', text: "La page **Supervision** affiche en direct l'état des agents (statut, inactivité), les **appels en cours**, et les indicateurs clés (agents en ligne, appels actifs, RDV du jour)." },
        { type: 'callout', variant: 'info', title: 'Périmètre', text: "Un **Superviseur** voit **son équipe** ; un **Administrateur/Propriétaire** voit tout le compte." },
        { type: 'callout', variant: 'warning', title: 'Alertes d\'inactivité', text: "Une cloche et un bandeau signalent les agents **inactifs** (en Disponible sans appel). C'est un outil de pilotage en direct." },
      ],
    },
    {
      id: 'ecoute',
      title: 'Écoute, souffleur, intervention',
      summary: 'Trois niveaux pour accompagner un agent sur un appel en cours.',
      icon: 'headphones',
      blocks: [
        { type: 'p', text: "Depuis un appel en cours, un superviseur peut s'y greffer de trois façons. **Votre poste téléphonique doit être configuré et connecté** (c'est lui qui reçoit l'audio)." },
        { type: 'table', headers: ['Mode', 'Qui vous entend', 'Usage'], rows: [
          ['**Écoute**', 'Personne (discret)', "Contrôle qualité silencieux."],
          ['**Souffleur**', "L'agent seulement", "Vous guidez l'agent sans que le client entende."],
          ['**Intervention**', 'Agent + client', "Vous prenez part à la conversation."],
        ] },
        { type: 'callout', variant: 'info', text: "L'écoute est **discrète** (aucun bip). Vous quittez l'écoute sans couper l'appel de l'agent." },
      ],
    },
    {
      id: 'temps',
      title: 'Temps des agents',
      summary: 'Combien de temps passé par statut, connecté, en appel.',
      icon: 'clock',
      blocks: [
        { type: 'p', text: "La page **Temps agents** calcule, sur n'importe quelle période, le **temps passé par statut** (Disponible, En appel, Pause…), avec un comparatif **Disponible vs En appel** et un journal groupé par jour." },
        { type: 'callout', variant: 'info', title: 'Fiabilité', text: "Le temps est calculé à partir d'un journal de statut où **une seule ligne est ouverte à la fois** par agent — pas de double comptage, pas d'« heures fantômes »." },
      ],
    },
    {
      id: 'stats',
      title: 'Statistiques',
      summary: 'Dispositions, KPIs, fiche qualifiée, export.',
      icon: 'bar',
      blocks: [
        { type: 'p', text: "La page **Statistiques** agrège les résultats : répartition des **dispositions** (global et par campagne), **KPIs** par agent et système, et la **fiche qualifiée** (leads positifs)." },
        { type: 'list', items: [
          "Filtres par agent, campagne, période — les KPIs suivent les filtres affichés.",
          "**Export CSV** des leads qualifiés (résultats positifs).",
        ] },
        { type: 'seealso', items: [
          { to: 'dispositions/types', label: 'Comprendre les types de dispositions' },
          { to: 'historique/cdr', label: "L'historique des appels" },
        ] },
      ],
    },
    {
      id: 'performance',
      title: 'La page Performance',
      summary: 'Vue visuelle : score, taux Vente/RDV, graphes, qualité des listes.',
      icon: 'trending-up',
      blocks: [
        { type: 'p', text: "La page **Performance** traduit l'activité en **graphes** et en un **score** synthétique par période (jour, semaine, mois…), filtrable par campagne et par agent." },
        { type: 'kv', items: [
          { k: 'Score', v: "Note globale de la période. Les **taux Vente et RDV** y pèsent le plus lourd — c'est la finalité du travail, avant le volume d'appels." },
          { k: 'Cartes KPI', v: "Appels passés/décrochés, **% de réponse**, durée moyenne, **conversion**, taux **Vente**, taux **RDV**, et rendez-vous réellement pris (dont **validés**)." },
          { k: 'Qualité des listes', v: "En bas de page : quelles listes de numéros produisent des résultats — pour arrêter ou recharger les bonnes listes." },
        ] },
        { type: 'callout', variant: 'info', title: 'Performance vs Statistiques', text: "**Statistiques** répond à « combien, en détail » (tableaux, exports). **Performance** répond à « est-ce que ça marche ? » d'un coup d'œil (tendances, score). Les deux lisent les mêmes données." },
        { type: 'seealso', items: [
          { to: 'suivi/rendez-vous', label: 'La page Rendez-vous' },
          { to: 'suivi/ventes', label: 'La page Ventes' },
        ] },
      ],
    },
    {
      id: 'liste-qualifiee',
      title: 'La Liste qualifiée (export des contacts)',
      summary: "Appels répondus + toutes les coordonnées, export CSV/Excel sur mesure.",
      icon: 'download',
      minRole: 'admin',
      blocks: [
        { type: 'p', text: "La **Liste qualifiée** (barre latérale, section Analyse) rassemble les **appels répondus** avec **toutes les informations de contact** (numéro, nom, société, e-mail, ville, adresse, champs importés…), leur **disposition**, leur **résultat** et l'**état de la fiche** — prêts à filtrer et à exporter." },
        { type: 'callout', variant: 'warning', title: 'Accès accordé par le propriétaire', text: "Le **Propriétaire** y accède toujours. Un **Administrateur** ne la voit que si le propriétaire lui a accordé l'accès (page **Utilisateurs** → compte admin → « Accès à la Liste qualifiée »). C'est la donnée la plus sensible du compte : coordonnées complètes + résultats, exportables." },
        { type: 'h', text: 'Filtres' },
        { type: 'list', items: [
          "**État de l'appel** : répondus (défaut), non répondus, ou tous les appelés.",
          "**Résultat** (type de disposition), **disposition précise**, **état de la fiche** (clôturé, à rappeler…).",
          "**Campagne**, **agent**, **client/prospect**, **période**, et recherche libre (numéro, nom, société, e-mail).",
        ] },
        { type: 'h', text: 'Export sur mesure' },
        { type: 'steps', items: [
          "Réglez vos filtres — l'export prend **toutes** les fiches filtrées (pas seulement celles affichées).",
          "Cliquez **Exporter…**, choisissez le **format** : Excel (.xlsx) mis en forme, CSV pour Excel (double-clic), ou CSV brut (imports/CRM).",
          "Cochez les **colonnes** voulues — « Champs personnalisés » ajoute une colonne par champ importé de vos fichiers (budget, note Google…).",
          "Téléchargez. Le nom du fichier est daté automatiquement.",
        ] },
        { type: 'seealso', items: [
          { to: 'supervision/stats', label: 'Statistiques (dispositions, KPIs)' },
          { to: 'dispositions/types', label: 'Les types de dispositions' },
        ] },
      ],
    },
    {
      id: 'mode-agent',
      title: 'Le mode agent (superviseurs)',
      summary: "Recevoir les appels comme un agent, puis revenir à son rôle d'un clic.",
      icon: 'headphones',
      blocks: [
        { type: 'p', text: "Le bouton **Mode agent** (barre du haut) permet à un superviseur, administrateur ou propriétaire de **travailler comme un agent** : tant qu'il est actif, vous recevez les appels automatiques selon vos assignations, et le menu affiche votre **Espace d'appel**." },
        { type: 'steps', items: [
          "Cliquez sur **Mode agent** dans la barre du haut — le bouton passe au vert « Mode agent actif ».",
          "Passez **Disponible** : vous recevez les appels comme n'importe quel agent.",
          "Re-cliquez pour revenir à votre rôle : vous ne recevez plus d'appels automatiques.",
        ] },
        { type: 'callout', variant: 'tip', title: 'Renfort ponctuel', text: "Pratique pour absorber un pic d'appels ou tester une campagne dans les conditions réelles d'un agent, sans créer de compte dédié." },
      ],
    },
  ],
}
